import { takeLatest, call, put, all } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { getFirebase } from 'react-redux-firebase';
import _forEach from 'lodash/forEach';
import _has from 'lodash/has';
import AlgoliaClient from 'utils/algoliaService';
import ContentfulClient from 'utils/contentfulService';
import Config from 'utils/getEnvConfig';
import { storeItemForSignIn } from 'containers/Auth/actions';
import { getColorAndColorName } from 'containers/Assessments/utils';
import { CREATE_LIST, GET_LISTS, DELETE_LIST, RENAME_LIST } from './constants';
import {
  processing,
  createListResult,
  getListsResult,
  deleteListSuccess,
  deleteListFail,
  renameListSuccess,
  renameListFail,
} from './actions';

const {
  ALGOLIA: { RESOURCES_AVG_RATED_INDEX },
} = Config;

function* createListSaga({ payload }) {
  try {
    yield put(processing(true));
    yield put(storeItemForSignIn({}));
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (error) {
      const {
        location: { pathname },
      } = payload;
      yield put(storeItemForSignIn({ type: 'saveToList', payload }));
      yield put(
        createListResult({
          added: false,
          error: true,
        }),
      );
      yield put(push(`/login?redirect=${pathname}`));
    }
    if (userID != null) {
      const { listID, listName, isPublic, resource, remove = false } = payload;
      if (listID) {
        const listDoc = yield getFirebase()
          .firestore()
          .collection('user_resources')
          .doc(userID)
          .collection('lists')
          .doc(listID)
          .get();
        let listResources = listDoc.data().resources;
        const resourceIds = resource.map(r => r.resourceID);
        if (
          listResources.filter(el => resourceIds.includes(el.resourceID))
            .length === resourceIds.length
        ) {
          if (remove) {
            listResources = listResources.filter(
              el => !resourceIds.includes(el.resourceID),
            );
            yield getFirebase()
              .firestore()
              .collection('user_resources')
              .doc(userID)
              .collection('lists')
              .doc(listID)
              .update({ resources: listResources });
            yield put(
              createListResult({
                removed: true,
                error: false,
                closeModal: false,
              }),
            );
          } else {
            yield put(
              createListResult({
                added: false,
                error: true,
                errorMessage: 'Resource is already in this list!',
              }),
            );
          }
        } else {
          const listResourcesIds = listResources.map(r => r.resourceID);
          resource.forEach(el => {
            if (!listResourcesIds.includes(el.resourceID))
              listResources.push(el);
          });
          yield getFirebase()
            .firestore()
            .collection('user_resources')
            .doc(userID)
            .collection('lists')
            .doc(listID)
            .update({ resources: listResources });
          yield put(
            createListResult({
              added: true,
              error: false,
              closeModal: false,
            }),
          );
        }
      } else {
        const list = {
          name: listName,
          isPublic,
          resources: resource,
        };
        yield getFirebase()
          .firestore()
          .collection('user_resources')
          .doc(userID)
          .collection('lists')
          .add(list);
        yield put(
          createListResult({
            created: true,
            added: true,
            error: false,
            closeModal: true,
          }),
        );
      }
    }
  } catch (error) {
    yield put(createListResult({ error: true, errorMessage: error }));
  } finally {
    yield put(processing(false));
  }
}

function* getListsSaga({ payload = false }) {
  try {
    yield put(processing(true));
    const userID = yield getFirebase().auth().currentUser.uid;
    const userListsDocs = yield getFirebase()
      .firestore()
      .collection('user_resources')
      .doc(userID)
      .collection('lists')
      .orderBy('name')
      .get();
    let lists = userListsDocs.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
    }));

    if (payload) {
      const resources = yield all(
        lists.map(el => call(getResourcesFromList, el)),
      );

      yield all(
        _forEach(resources, (value, index) => {
          lists[index].resources = value;
        }),
      );
    }
    lists = yield all(
      lists.map(el => (el.isPublic ? getEditorFromMyList(el) : el)),
    );
    lists = lists.filter(el => el !== null);
    yield put(getListsResult(lists));
  } catch (error) {
    yield put(getListsResult([]));
  } finally {
    yield put(processing(false));
  }
}

function* deleteListSaga({ payload }) {
  try {
    yield put(processing(true));
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (error) {
      yield put(push(`/login?redirect=/`));
    }
    if (userID != null) {
      const { listID, allLists } = payload;
      if (listID) {
        yield getFirebase()
          .firestore()
          .collection('user_resources')
          .doc(userID)
          .collection('lists')
          .doc(listID)
          .delete();

        const publicListDoc = yield getFirebase()
          .firestore()
          .collection('public_lists')
          .doc(listID);
        if (publicListDoc.exists) publicListDoc.update({ active: false });

        const newAllLists = allLists.filter(el => el.id !== listID);
        yield put(
          deleteListSuccess({
            result: {
              removed: true,
              error: false,
            },
            newAllLists,
          }),
        );
      } else {
        yield put(
          deleteListFail({
            result: {
              removed: false,
              error: true,
            },
          }),
        );
      }
    }
  } catch (error) {
    yield put(deleteListFail({ result: { error: true, errorMessage: error } }));
  } finally {
    yield put(processing(false));
  }
}

function* renameListSaga({ payload }) {
  try {
    yield put(processing(true));
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (error) {
      yield put(push(`/login?redirect=/`));
    }
    if (userID != null) {
      const { listID, allLists, newName } = payload;
      if (listID) {
        yield getFirebase()
          .firestore()
          .collection('user_resources')
          .doc(userID)
          .collection('lists')
          .doc(listID)
          .update({ name: newName });

        const newAllLists = allLists.filter(el => el.id !== listID);
        const newList = allLists.find(el => el.id === listID);
        newList.name = newName;
        newAllLists.push(newList).sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
        yield put(
          renameListSuccess({
            result: {
              renamed: true,
              error: false,
            },
            newAllLists,
          }),
        );
      } else {
        yield put(
          renameListFail({
            result: {
              renamed: false,
              error: true,
            },
          }),
        );
      }
    }
  } catch (error) {
    yield put(renameListFail({ result: { error: true, errorMessage: error } }));
  } finally {
    yield put(processing(false));
  }
}

function* getResourcesFromList(list) {
  const AlgoliaIndex = AlgoliaClient.initIndex(RESOURCES_AVG_RATED_INDEX);

  const itemIds = list.resources.map(resource => resource.resourceID);

  let idsQuery = 'AND ';
  _forEach(itemIds, (value, index) => {
    idsQuery += `objectID:'${value}'`;
    if (index !== itemIds.length - 1) {
      idsQuery += ' OR ';
    }
  });

  const queryParams = {
    hitsPerPage: 100,
    filters: `reviewStatus:'Accepted' ${idsQuery}`,
  };
  const [resourcesResponse, assessmentsResponse] = yield all([
    call(() => AlgoliaIndex.search('', queryParams)),
    call(() =>
      ContentfulClient.getEntries({
        content_type: 'assessments',
        // 'sys.id[in]': itemIds.join(),
        'fields.reviewStatus': 'Accepted',
        order: '-fields.userRating,fields.name',
        include: 2,
        limit: 1000,
      }),
    ),
  ]);

  const assessmentData = assessmentsResponse.items
    .map(el => {
      const { color, colorName } = getColorAndColorName(el);
      return {
        ...el,
        color,
        colorName,
      };
    })
    .filter(el => itemIds.includes(el.sys.id));
  const resources = [];
  resourcesResponse.hits.forEach(el => resources.push(el));
  assessmentData.forEach(el => resources.push({ ...el, type: 'Assessments' }));

  return resources;
}

function* getEditorFromMyList(list) {
  try {
    const listWithEditor = { ...list };
    const publicListDoc = yield getFirebase()
      .firestore()
      .collection('public_lists')
      .doc(list.id)
      .get();
    const publicList = publicListDoc.data();

    const editorFromContentful = publicList.userID !== publicList.editorID;
    const id = publicList.editorID || publicList.userID;

    if (editorFromContentful) {
      const editorResponse = yield call(() =>
        ContentfulClient.getEntries({
          content_type: 'editors',
          'sys.id': id,
        }),
      );
      listWithEditor.editorName = editorResponse.items[0].fields.name;
      listWithEditor.editorImage = _has(editorResponse.items[0].fields, 'image')
        ? editorResponse.items[0].fields.image.fields.file.url
        : null;

      return listWithEditor;
    }

    listWithEditor.editorName = publicList.username;
    listWithEditor.editorImage = publicList.avatar;

    return listWithEditor;
  } catch (error) {
    return null;
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(CREATE_LIST, createListSaga);
  yield takeLatest(GET_LISTS, getListsSaga);
  yield takeLatest(DELETE_LIST, deleteListSaga);
  yield takeLatest(RENAME_LIST, renameListSaga);
}
